import { useState, useEffect } from "react";
import { Search, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDebounce } from "@/hooks/useDebounce";

interface Property {
  id: number;
  attributes: {
    propertyType: string;
    type: string;
    status: string;
    apartmentNumber: string;
    numberOfBedrooms: string;
    price: number;
    street: string;
    number: string;
    tagKanlo: string;
  };
}

interface AddPropertyToBlockDialogProps {
  blockId: number;
  condominiumId: number;
  blockName: string;
  onPropertyAdded: () => void;
  trigger: React.ReactNode;
}

export function AddPropertyToBlockDialog({
  blockId,
  condominiumId,
  blockName,
  onPropertyAdded,
  trigger,
}: AddPropertyToBlockDialogProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null,
  );
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const route = import.meta.env.VITE_URL_ENDPOINT;

  // Buscar propriedades por TagKenlo
  const searchProperties = async (term: string) => {
    if (!term || term.length < 3) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${route}/api/properties?filters[tagKanlo][$containsi]=${term}&filters[block][$null]=true&populate=*`,
      );
      const result = await response.json();
      setSearchResults(result.data || []);
    } catch (error) {
      console.error("Erro ao buscar propriedades:", error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Effect para buscar quando o termo de busca mudar
  useEffect(() => {
    if (debouncedSearchTerm) {
      searchProperties(debouncedSearchTerm);
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearchTerm]);

  const handleAddPropertyToBlock = async () => {
    if (!selectedProperty) return;

    try {
      const response = await fetch(
        `${route}/api/properties/${selectedProperty.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            data: {
              block: blockId,
              condominium: condominiumId,
            },
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Falha ao adicionar propriedade ao bloco");
      }

      onPropertyAdded();
      setOpen(false);
      setSearchTerm("");
      setSelectedProperty(null);
      setSearchResults([]);
    } catch (error) {
      console.error("Erro ao adicionar propriedade ao bloco:", error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Adicionar Imóvel ao {blockName}</DialogTitle>
          <DialogDescription>
            Busque por imóveis usando a TagKenlo para adicionar ao bloco
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="search">Buscar por TagKenlo</Label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="search"
                placeholder="Digite a TagKenlo do imóvel..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Resultados da busca */}
          {loading && (
            <div className="flex items-center justify-center py-4">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="max-h-60 space-y-2 overflow-y-auto">
              <Label>Resultados da busca:</Label>
              {searchResults.map((property) => (
                <div
                  key={property.id}
                  className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                    selectedProperty?.id === property.id
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                  onClick={() => setSelectedProperty(property)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {property.attributes.type ||
                          property.attributes.propertyType}
                        {property.attributes.apartmentNumber &&
                          ` - Apto ${property.attributes.apartmentNumber}`}
                      </h4>
                      <p className="text-sm text-gray-600">
                        TagKenlo: {property.attributes.tagKanlo}
                      </p>
                      <p className="text-sm text-gray-600">
                        Quartos: {property.attributes.numberOfBedrooms || "N/A"}
                      </p>
                    </div>
                    <div className="text-right">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          property.attributes.status === "À venda"
                            ? "bg-blue-100 text-blue-800"
                            : property.attributes.status === "Vendido"
                              ? "bg-red-100 text-red-800"
                              : property.attributes.status === "Locação"
                                ? "bg-purple-100 text-purple-800"
                                : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {property.attributes.status}
                      </span>
                      {property.attributes.price && (
                        <p className="mt-1 text-sm font-medium text-green-600">
                          {formatCurrency(property.attributes.price)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {searchTerm &&
            searchTerm.length >= 3 &&
            !loading &&
            searchResults.length === 0 && (
              <p className="text-center text-gray-500">
                Nenhum imóvel encontrado com essa TagKenlo
              </p>
            )}
        </div>

        <DialogFooter>
          <Button
            onClick={handleAddPropertyToBlock}
            disabled={!selectedProperty}
            className="flex items-center gap-2"
          >
            <Plus size={16} />
            Adicionar ao Bloco
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
